import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";
import { ProjectSchedulesService } from "@/modules/projects/service/projectSchedule";
import { ProjectSchedulesDeleteFormData } from "@/types/projectSchedulesDeleteForm";
import { useProjectSchedulesDeleteForm } from "@/modules/projects/hooks/useProjectSchedulesDeleteForm";
import { BATCH_SIZE } from "@/modules/projects/components/schedules/constants";

interface UseProjectSchedulesDeleteFormWithBatchOptions {
  projectId: string;
  dateStart: string;
  dateEnd: string;
  getSelection: () => ReadonlySet<string>;
  onSuccess: () => void;
  batchSize?: number;
}

// Runs schedule delete in sequential API batches to avoid request timeouts on large selections.
export const useProjectSchedulesDeleteFormWithBatch = ({
  projectId,
  dateStart,
  dateEnd,
  getSelection,
  onSuccess,
  batchSize = BATCH_SIZE,
}: UseProjectSchedulesDeleteFormWithBatchOptions) => {
  const { t } = useTranslation();
  const operationProgress = useAsyncOperationProgress();

  const processBatched = useCallback(
    async (values: ProjectSchedulesDeleteFormData) => {
      const scheduleIds = Array.from(getSelection());
      const dateRange = values.delete_all
        ? undefined
        : { from_date: values.date_start, to_date: values.date_end };

      const batches: string[][] = [];
      for (let i = 0; i < scheduleIds.length; i += batchSize) {
        batches.push(scheduleIds.slice(i, i + batchSize));
      }

      const batchItems = batches.map((batch, index) => ({
        id: `batch-${index}`,
        label: t("projectSchedules.progress.batch", {
          number: index + 1,
          count: batch.length,
        }),
      }));

      operationProgress.initialize(batchItems);
      operationProgress.startProcessing();

      const errors: { batchIndex: number; error: Error }[] = [];

      for (let i = 0; i < batches.length; i++) {
        if (operationProgress.isCancelling()) {
          break;
        }

        const batch = batches[i];
        const batchItem = batchItems[i];

        operationProgress.setCurrentItem(batchItem.label);

        try {
          await ProjectSchedulesService.deleteSchedules(
            projectId,
            batch,
            dateRange,
          );
          operationProgress.markItemProcessed(batchItem.id, true);
        } catch (error) {
          operationProgress.markItemProcessed(
            batchItem.id,
            false,
            error as Error,
          );
          errors.push({ batchIndex: i, error: error as Error });
        }
      }

      operationProgress.complete();

      return {
        totalBatches: batches.length,
        successfulBatches: batches.length - errors.length,
        failedBatches: errors.length,
        errors,
      };
    },
    [batchSize, getSelection, operationProgress, projectId, t],
  );

  const mutation = useMutation({
    mutationFn: processBatched,
    onSuccess: () => {
      onSuccess();
      form.reset();
    },
    onError: () => {
      operationProgress.complete();
    },
  });

  const { form, defaultValues } = useProjectSchedulesDeleteForm({
    dateStart,
    dateEnd,
    disabled: mutation.isPending,
  });

  const cancel = useCallback(() => {
    operationProgress.cancel();
  }, [operationProgress]);

  return {
    form,
    defaultValues,
    mutation,
    operationProgress,
    cancel,
  };
};
